import React from 'react';

import * as Yup from 'yup';

import { ImpactSelector, ProbabilitySelector } from '../../radio/register/Selectors';

import TextFormField from 'CommonComponents/TextFormField';

/** Window event: the items of a node changed, so an open list modal re-fetches. */
export const RISK_NODE_ITEMS_CHANGED = 'riskNodeItemsChanged';

/**
 * The fields a grid risk or opportunity carries beyond name, description,
 * amount and AFC: a risk's register impact and probability (set together or
 * not at all), an opportunity's weighting.
 */
const ratingRules = {
    general_impact_id: Yup.string()
        .nullable()
        .when('general_probability_id', {
            is: (value) => !!value,
            then: Yup.string().required($t('This value is required when probability is selected')),
        }),
    general_probability_id: Yup.string()
        .nullable()
        .when('general_impact_id', {
            is: (value) => !!value,
            then: Yup.string().required($t('This value is required when impact is selected')),
        }),
};

export function riskNodeItemSchema(type, base) {
    return type === 'risk'
        ? base.shape(ratingRules, [['general_impact_id', 'general_probability_id']])
        : base.shape({
              weighting_percentage: Yup.number()
                  .transform((value, original) => (original === '' ? null : value))
                  .nullable()
                  .integer()
                  .min(0)
                  .max(100),
          });
}

/** Initial values of the extra fields, from an existing entry when editing. */
export function riskNodeItemInitialValues(type, entry = null) {
    return type === 'risk'
        ? { general_impact_id: entry?.general_impact_id ?? '', general_probability_id: entry?.general_probability_id ?? '' }
        : { weighting_percentage: entry?.weighting_percentage ?? '' };
}

/** Drops the other type's fields and sends a cleared rating or weighting as null. */
export function riskNodeItemPayload(type, values) {
    const { general_impact_id, general_probability_id, weighting_percentage, ...rest } = values;
    const orNull = (value) => (value === '' || value === undefined ? null : value);

    return type === 'risk'
        ? { ...rest, general_impact_id: orNull(general_impact_id), general_probability_id: orNull(general_probability_id) }
        : { ...rest, weighting_percentage: orNull(weighting_percentage) };
}

/**
 * `ratingsEditable` is false for a risk with no register record: it has nowhere to
 * keep an impact or probability, so the selectors are not offered.
 */
export function RiskNodeItemFields({ type, impacts, probabilities, ratingsEditable = true }) {
    if (type === 'risk') {
        if (!ratingsEditable) {
            return null;
        }

        return (
            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                <ImpactSelector label={$t('Impact')} name="general_impact_id" impacts={impacts} />
                <ProbabilitySelector label={$t('Probability')} name="general_probability_id" probabilities={probabilities} />
            </div>
        );
    }

    return (
        <div className="tw-flex tw-flex-col tw-space-y-1.5">
            <TextFormField label={$t('Weighting %')} type="number" name="weighting_percentage" min="0" max="100" />
        </div>
    );
}
